import isMobileLayout from '../utils/isMobileLayout';
import {
  GoogleMap,
  OverlayView,
  Polyline,
  Circle,
  useLoadScript
} from '@react-google-maps/api';

import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';

import onlineIcon from '../assets/icons/divi-marcadores/online.svg';
import offlineIcon from '../assets/icons/divi-marcadores/offline.svg';
import staleIcon from '../assets/icons/divi-marcadores/stale.svg';
import sinDatosIcon from '../assets/icons/divi-marcadores/sindatos.svg';
import seleccionadoIcon from '../assets/icons/divi-marcadores/seleccionado.svg';

import VehicleArcMenu from './VehicleArcMenu/VehicleArcMenu';
import VehicleLocationBar from './VehicleLocationBar';

import useUserLocation from './GoogleMapView/hooks/useUserLocation';
import UserLocationMarker from './GoogleMapView/components/UserLocationMarker';
import TrackingNavigationBar from './GoogleMapView/components/TrackingNavigationBar';
const containerStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%'
};

const center = {
  lat: 3.4516,
  lng: -76.5320
};

const VEHICLE_LIVE_OFFSET = 0;
const VEHICLE_PLAYBACK_OFFSET = 0;

const GOOGLE_MAPS_LIBRARIES = [
  'marker'
];

function getVehicleMapKey(vehicle) {
  return String(
    vehicle?.imei ||
    vehicle?.uniqueId ||
    vehicle?.id ||
    vehicle?.name ||
    ''
  );
}

function GoogleMapView({
  onDiviMapReady,
  vehicles = [],
  historialRuta = [],
  playbackIndex = 0,
  playbackPosition,
  playbackTravelledPath = [],
  detencionesHistoricas = [],
  vehiculoSeleccionado,
  setVehiculoSeleccionado,
  selectionToken = 0,
  modoHistoricoActivo = false,
  detalleVehiculoVisible = false,

  onOpenVehicleDetail,
  onOpenHistory,
  onOpenEvents,
  onOpenGeofences,
  onOpenTracking,
  onOpenReports,
  onOpenSentinel,
ocultarMenuGeocercas = false,
radioGeocercaAmplia = false,
  modoCrearGeocerca = false,
  geocercaTemporal,
  setGeocercaTemporal,
  geocercas = []
}) {
  const [map, setMap] = useState(null);
const [mapZoom, setMapZoom] =
  useState(13);
const [mapType, setMapType] = useState('roadmap');
const liveMarkerHeadingNodesRef = useRef(new Map());
const compassNeedleRef = useRef(null);
const manualCameraNotifiedRef = useRef(false);
const rotationContextRef = useRef(null);
const applyCameraHeading = useCallback((heading) => {
  mapHeadingRef.current = heading;
  // Los OverlayView son planos: corregir su orientación sin renderizar React.
  for (const {node, course} of liveMarkerHeadingNodesRef.current.values()) {
    const rotation = ((course - heading) % 360 + 360) % 360;
    node.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
  }
  if (compassNeedleRef.current) {
    compassNeedleRef.current.setAttribute('transform', `rotate(${-heading} 16 17)`);
  }
}, []);
const mapHeadingRef = useRef(0);
const [returnToAssetVisible, setReturnToAssetVisible] = useState(false);

// El histórico conserva su cámara y sus flechas orientadas al norte.
useEffect(() => {
  if (!map || !modoHistoricoActivo) return;
  mapHeadingRef.current = 0;
  applyCameraHeading(0);
  setReturnToAssetVisible(false);
  map.setHeading(0);
  map.setTilt(0);
}, [map, modoHistoricoActivo, applyCameraHeading]);


const [streetViewActive, setStreetViewActive] = useState(false);

const [
  streetViewMessage,
  setStreetViewMessage
] = useState('');
const {
  userLocation,
  userLocationAccuracy,
  userLocationError
} = useUserLocation();

const [
  locationBarVisible,
  setLocationBarVisible
] = useState(false);

const [
  trackingNavigationVisible,
  setTrackingNavigationVisible
] = useState(false);

const [
  locationLoading,
  setLocationLoading
] = useState(false);

const [
  vehicleLocation,
  setVehicleLocation
] = useState(null);

const locationRequestRef = useRef(0);

const lastLocationSelectionRef =
  useRef(0);
const mapaAntesStreetViewRef = useRef(null);


const abrirStreetView = () => {
  if (!map || !vehiculoSeleccionado) {
    return;
  }

  const lat =
    vehiculoSeleccionado.lat ??
    vehiculoSeleccionado.latitude;

  const lng =
    vehiculoSeleccionado.lng ??
    vehiculoSeleccionado.longitude;

  if (lat == null || lng == null) {
    return;
  }

  const latitude = Number(lat);
  const longitude = Number(lng);

  if (
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    return;
  }

  /*
    Antes de abrir Street View verificamos
    si Google realmente tiene un panorama
    disponible cerca del activo.
  */
  const streetViewService =
    new window.google.maps.StreetViewService();

  setStreetViewMessage('');

  streetViewService.getPanorama(
    {
      location: {
        lat: latitude,
        lng: longitude
      },

      radius: 150,

      source:
        window.google.maps
          .StreetViewSource.OUTDOOR
    },

    (data, status) => {
      if (
        status !==
          window.google.maps
            .StreetViewStatus.OK ||
        !data?.location?.latLng
      ) {
        /*
          No abrimos Street View para evitar
          la pantalla negra.
        */
        setStreetViewMessage(
          'Street View no disponible en esta ubicación'
        );

        setTimeout(() => {
          setStreetViewMessage('');
        }, 3500);

        return;
      }

      const center =
        map.getCenter();

      const zoom =
        map.getZoom();

      const projection =
        map.getProjection();

      const bounds =
        map.getBounds();

      const div =
        map.getDiv();

      let assetPixel = null;

      if (
        projection &&
        bounds &&
        div
      ) {
        const ne =
          bounds.getNorthEast();

        const sw =
          bounds.getSouthWest();

        const topRight =
          projection.fromLatLngToPoint(
            ne
          );

        const bottomLeft =
          projection.fromLatLngToPoint(
            sw
          );

        const assetPoint =
          projection.fromLatLngToPoint(
            new window.google.maps.LatLng(
              latitude,
              longitude
            )
          );

        if (
          topRight &&
          bottomLeft &&
          assetPoint
        ) {
          const scale =
            Math.pow(2, zoom);

          assetPixel = {
            x:
              (assetPoint.x -
                bottomLeft.x) *
              scale,

            y:
              (assetPoint.y -
                topRight.y) *
              scale
          };
        }
      }

      mapaAntesStreetViewRef.current = {
        center: center
          ? {
              lat: center.lat(),
              lng: center.lng()
            }
          : null,

        zoom,
        heading: Number(map.getHeading()) || 0,
        tilt: Number(map.getTilt()) || 0,
        assetPixel
      };

      const panorama =
        map.getStreetView();

      panorama.setOptions({
        addressControl: false,
        linksControl: true,
        panControl: false,
        zoomControl: false,
        fullscreenControl: false,
        enableCloseButton: false
      });

      /*
        Importante:
        usamos la posición real del panorama
        encontrado por Google, no necesariamente
        la coordenada exacta del vehículo.
      */
      panorama.setPosition(
        data.location.latLng
      );

      panorama.setPov({
        heading: 0,
        pitch: 0
      });

      panorama.setVisible(true);
    }
  );
};


const [
  vehiculosAnimados,
  setVehiculosAnimados
] = useState({});

/*
  Permite consultar la posición visual actual
  del vehículo sin ejecutar el seguimiento
  en cada frame de animación.
*/
const vehiculosAnimadosRef =
  useRef({});

/*
  Evita correcciones consecutivas de cámara.
*/
const lastCameraAdjustmentRef =
  useRef(0);

useEffect(() => {
  vehiculosAnimadosRef.current =
    vehiculosAnimados;
}, [vehiculosAnimados]);

const [
  playbackFollow,
  setPlaybackFollow
] = useState(true);

const [
  liveVehicleFollow,
  setLiveVehicleFollow
] = useState(false);

const playbackStartedRef =
  useRef(false);
  const playbackManualControlRef =
  useRef(false);
  const playbackPositionRef =
  useRef(null);
  const playbackZoomTimerRef =
  useRef(null);

useEffect(() => {
  playbackPositionRef.current =
    playbackPosition;
}, [playbackPosition]);
useEffect(() => {
  if (!map) {
    return;
  }

  const iniciarSeguimientoHistorico = () => {
    /*
      Solamente el primer Play hace zoom.
    */
    if (playbackStartedRef.current) {
      return;
    }

    if (playbackManualControlRef.current) {
      return;
    }

    const posicionActual =
      playbackPositionRef.current;

    if (!posicionActual) {
      return;
    }

    const lat =
      Number(posicionActual.lat);

    const lng =
      Number(posicionActual.lng);

    if (
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) {
      return;
    }

    if (playbackZoomTimerRef.current) {
      clearTimeout(
        playbackZoomTimerRef.current
      );

      playbackZoomTimerRef.current =
        null;
    }

    playbackStartedRef.current = true;

    lastCameraAdjustmentRef.current = 0;

    setPlaybackFollow(true);

    /*
      Evita que los cambios automáticos
      desactiven el seguimiento.
    */
    ignorarEventoMapaRef.current = true;

    /*
      Primero movemos suavemente la cámara
      hasta el vehículo.
    */
    map.panTo({
      lat,
      lng
    });

    const zoomInicial =
      Number(map.getZoom()) || 13;

    /*
      16.5 permite visualizar calles y recorrido
      sin acercarse exageradamente.
    */
    const zoomDestino =
      Math.max(
        zoomInicial,
        16.5
      );

    const cantidadPasos = 7;

    let pasoActual = 0;

    const animarZoom = () => {
      pasoActual += 1;

      const progreso =
        pasoActual /
        cantidadPasos;

      /*
        Inicio suave y final progresivo.
      */
      const progresoSuavizado =
        1 -
        Math.pow(
          1 - progreso,
          3
        );

      const nuevoZoom =
        zoomInicial +
        (
          zoomDestino -
          zoomInicial
        ) *
        progresoSuavizado;

      map.setZoom(
        nuevoZoom
      );

      if (
        pasoActual <
        cantidadPasos
      ) {
        playbackZoomTimerRef.current =
          setTimeout(
            animarZoom,
            115
          );

        return;
      }

      map.setZoom(
        zoomDestino
      );

      playbackZoomTimerRef.current =
        setTimeout(() => {
          ignorarEventoMapaRef.current =
            false;

          playbackZoomTimerRef.current =
            null;
        }, 250);
    };

    /*
      Pequeña pausa para apreciar primero
      el encuadre completo y después
      el acercamiento.
    */
    playbackZoomTimerRef.current =
      setTimeout(
        animarZoom,
        140
      );
  };

  window.addEventListener(
    'maccez:history-playback-start',
    iniciarSeguimientoHistorico
  );

  return () => {
    window.removeEventListener(
      'maccez:history-playback-start',
      iniciarSeguimientoHistorico
    );

    if (playbackZoomTimerRef.current) {
      clearTimeout(
        playbackZoomTimerRef.current
      );

      playbackZoomTimerRef.current =
        null;
    }
  };
}, [map]);

const ignorarEventoMapaRef =
  useRef(false);

const lastLiveFollowPositionRef =
  useRef(null);

const lastSelectedIdRef =
  useRef(null);

/*
  Evita recalcular el encuadre inicial
  con cada actualización GPS.
*/
const initialViewportAppliedRef =
  useRef(false);

/*
  Controla la animación de acercamiento.
*/
const vehicleZoomTimerRef =
  useRef(null);

/*
  Permite distinguir una selección hecha
  por el usuario de la selección inicial.
*/
const lastCameraSelectionTokenRef =
  useRef(0);
  const targetsRef = useRef({});
  const animationRef = useRef(null);
  const cursosRef = useRef({});
  const cursosAnimadosRef = useRef({});
  const ultimasPosicionesRef = useRef({});

const { isLoaded } = useLoadScript({
  googleMapsApiKey:
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,

  libraries:
    GOOGLE_MAPS_LIBRARIES
});
useEffect(() => {
  const cerrarBarrasParaHub = () => {
    setLocationBarVisible(false);

    setTrackingNavigationVisible(false);
  };

  window.addEventListener(
    'maccez:mobile-hub-open',
    cerrarBarrasParaHub
  );

  return () => {
    window.removeEventListener(
      'maccez:mobile-hub-open',
      cerrarBarrasParaHub
    );
  };
}, []);

useEffect(() => {
  if (!detalleVehiculoVisible) {
    return;
  }

  /*
    El detalle del vehículo toma el control
    de la zona inferior del mapa.
  */
  setLocationBarVisible(false);
  setTrackingNavigationVisible(false);
  setStreetViewMessage('');

  /*
    También cerramos las opciones abiertas
    del botón central MACCEZ.
  */
  window.dispatchEvent(
    new CustomEvent(
      'maccez:mobile-hub-close'
    )
  );
}, [detalleVehiculoVisible]);

useEffect(() => {
  if (!map) return;

  const panorama = map.getStreetView();

  const listener = panorama.addListener(
    'visible_changed',
    () => {
      setStreetViewActive(
        panorama.getVisible()
      );
    }
  );

  return () => {
    listener.remove();
  };
}, [map]);
  const rutaHistorica = useMemo(() => {
    return historialRuta
      .map((p) => ({
        lat: Number(p.lat ?? p.latitude ?? p[0]),
        lng: Number(p.lng ?? p.longitude ?? p[1])
      }))
      .filter(
        (p) =>
          !Number.isNaN(p.lat) &&
          !Number.isNaN(p.lng) &&
          p.lat !== 0 &&
          p.lng !== 0
      );
  }, [historialRuta]);

  const marcadoresHistoricos = useMemo(() => {
    if (!historialRuta || historialRuta.length < 2) {
      return {
        inicio: null,
        fin: null,
        paradas: []
      };
    }

    const ruta = historialRuta
      .map((p) => ({
        lat: Number(p.lat ?? p.latitude),
        lng: Number(p.lng ?? p.longitude),
        fecha: p.fecha ? new Date(p.fecha) : null,
        ignition: p.ignition ?? null,
        din1: p.din1 ?? null
      }))
      .filter(
        (p) =>
          !Number.isNaN(p.lat) &&
          !Number.isNaN(p.lng) &&
          p.lat !== 0 &&
          p.lng !== 0 &&
          p.fecha &&
          !Number.isNaN(p.fecha.getTime())
      );

    if (ruta.length < 2) {
      return {
        inicio: null,
        fin: null,
        paradas: []
      };
    }

    const inicioRuta = ruta[0];
    const finRuta = ruta[ruta.length - 1];

    const obtenerEstado = (p) => {
      if (p.ignition === 'ENCENDIDO' || Number(p.din1) === 1) {
        return 'ENCENDIDO';
      }

      if (p.ignition === 'APAGADO' || Number(p.din1) === 0) {
        return 'APAGADO';
      }

      return 'N/D';
    };

    const formatearDuracion = (segundos) => {
      const totalMin = Math.round(segundos / 60);
      const horas = Math.floor(totalMin / 60);
      const minutos = totalMin % 60;

      if (horas > 0) return `${horas}h ${minutos} min`;
      return `${minutos} min`;
    };

    const paradasDetectadas = [];

    let estadoActual = obtenerEstado(ruta[0]);
    let inicioEstado = ruta[0].fecha;
    let puntoInicioEstado = ruta[0];

    for (let i = 1; i < ruta.length; i++) {
      const estado = obtenerEstado(ruta[i]);

      if (estado !== estadoActual) {
        const finEstado = ruta[i].fecha;
        const duracionSeg =
          (finEstado.getTime() - inicioEstado.getTime()) / 1000;

        if (estadoActual === 'APAGADO' && duracionSeg >= 180) {
          paradasDetectadas.push({
            lat: puntoInicioEstado.lat,
            lng: puntoInicioEstado.lng,
            inicio: inicioEstado,
            fin: finEstado,
            duracion: formatearDuracion(duracionSeg)
          });
        }

        estadoActual = estado;
        inicioEstado = ruta[i].fecha;
        puntoInicioEstado = ruta[i];
      }
    }

    if (estadoActual === 'APAGADO') {
      const duracionSeg =
        (finRuta.fecha.getTime() - inicioEstado.getTime()) / 1000;

      if (duracionSeg >= 180) {
        paradasDetectadas.push({
          lat: puntoInicioEstado.lat,
          lng: puntoInicioEstado.lng,
          inicio: inicioEstado,
          fin: finRuta.fecha,
          duracion: formatearDuracion(duracionSeg)
        });
      }
    }

    const distanciaMetros = (a, b) => {
      const R = 6371000;
      const toRad = (v) => (v * Math.PI) / 180;

      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);

      const x =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(a.lat)) *
          Math.cos(toRad(b.lat)) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);

      const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));

      return R * c;
    };

    const paradasIntermedias = paradasDetectadas
      .filter((p) => {
        const distanciaInicio = distanciaMetros(p, inicioRuta);
        const distanciaFin = distanciaMetros(p, finRuta);

        return distanciaInicio > 40 && distanciaFin > 40;
      })
      .map((p, index) => ({
        ...p,
        numero: index + 1
      }));

    return {
      inicio: inicioRuta,
      fin: finRuta,
      paradas: paradasIntermedias
    };
  }, [historialRuta]);

  function normalizarAngulo(angulo) {
    return ((angulo % 360) + 360) % 360;
  }

  function calcularCoursePlayback(ruta, index) {
    const actual = ruta[index];
    const siguiente = ruta[index + 1] || ruta[index - 1];

    if (!actual || !siguiente) return 0;

    const lat1 = (actual.lat * Math.PI) / 180;
    const lat2 = (siguiente.lat * Math.PI) / 180;
    const dLng = ((siguiente.lng - actual.lng) * Math.PI) / 180;

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    const brng = (Math.atan2(y, x) * 180) / Math.PI;

    return normalizarAngulo(brng);
  }

  function calcularCourseEntrePuntos(origen, destino) {
    if (!origen || !destino) return null;

    const lat1 = (origen.lat * Math.PI) / 180;
    const lat2 = (destino.lat * Math.PI) / 180;
    const dLng = ((destino.lng - origen.lng) * Math.PI) / 180;

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    const brng = (Math.atan2(y, x) * 180) / Math.PI;

    return normalizarAngulo(brng);
  }

  function interpolarAngulo(actual, destino, factor = 0.15) {
    actual = normalizarAngulo(actual);
    destino = normalizarAngulo(destino);

    let diferencia = destino - actual;

    if (diferencia > 180) diferencia -= 360;
    if (diferencia < -180) diferencia += 360;

    return normalizarAngulo(actual + diferencia * factor);
  }

  function calcularDistanciaMetros(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = (value) => (value * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c =
      2 *
      Math.atan2(
        Math.sqrt(a),
        Math.sqrt(1 - a)
      );

    return Math.round(R * c);
  }

 function getVehicleIcon(vehicle) {
  const connectionStatus =
    String(
      vehicle.connectionStatus ||
      vehicle.status ||
      'OFFLINE'
    ).toUpperCase();

  const ignitionOff =
    vehicle.ignition === false ||
    vehicle.ignition === 0 ||
    vehicle.ignition === '0' ||
    vehicle.ignition === 'false' ||
    vehicle.ignition === 'OFF' ||
    vehicle.ignition === 'APAGADO';

  const rawSecondsAgo =
    vehicle.secondsAgo;

  const secondsAgo =
    rawSecondsAgo === null ||
    rawSecondsAgo === undefined ||
    rawSecondsAgo === ''
      ? null
      : Number(rawSecondsAgo);

  const hasValidSecondsAgo =
    Number.isFinite(secondsAgo) &&
    secondsAgo >= 0;

  /*
    Verde: conectado.
  */
  if (
    connectionStatus === 'ONLINE'
  ) {
    return onlineIcon;
  }

  /*
    Naranja: señal antigua.
  */
  if (
    connectionStatus === 'STALE'
  ) {
    return staleIcon;
  }

  /*
    Gris: nunca ha reportado.
  */
  if (
    connectionStatus === 'SIN_REPORTE'
  ) {
    return sinDatosIcon;
  }

  if (
    connectionStatus === 'OFFLINE'
  ) {
    /*
      Azul: en reposo y reportó durante
      las últimas dos horas.
    */
    if (
      ignitionOff &&
      hasValidSecondsAgo &&
      secondsAgo <= 2 * 60 * 60
    ) {
      return seleccionadoIcon;
    }

    /*
      Gris: ignición apagada, pero lleva
      más de dos horas sin comunicación
      o no existe un tiempo válido.
    */
    if (ignitionOff) {
      return sinDatosIcon;
    }

    /*
      Rojo: perdió comunicación sin tener
      confirmación de ignición apagada.
    */
    return offlineIcon;
  }

  /*
    Cualquier estado desconocido conserva
    el mismo resultado rojo del menú.
  */
  return offlineIcon;
}
  function obtenerComponenteDireccion(
  components,
  tipos
) {
  if (!Array.isArray(components)) {
    return '';
  }

  for (const tipo of tipos) {
    const encontrado =
      components.find((component) =>
        component.types?.includes(tipo)
      );

    if (encontrado) {
      return encontrado.long_name || '';
    }
  }

  return '';
}

async function obtenerDireccionVehiculo(
  lat,
  lng
) {
  if (!window.google?.maps?.Geocoder) {
    return;
  }

  const requestId =
    ++locationRequestRef.current;

  setLocationLoading(true);
  setVehicleLocation(null);

  try {
    const geocoder =
      new window.google.maps.Geocoder();

    const response =
      await geocoder.geocode({
        location: {
          lat: Number(lat),
          lng: Number(lng)
        }
      });

    if (
      requestId !==
      locationRequestRef.current
    ) {
      return;
    }

   const results =
  response?.results || [];

const principal =
  results[0];

if (!principal) {
  setVehicleLocation(null);
  return;
}

/*
  Detecta los Plus Codes de Google.

  Ejemplos:
  6CHX+63
  7JQ5+8R
  849VCWC8+R9

  No queremos mostrar estos códigos
  directamente al usuario.
*/
const esPlusCode = (value = '') => {
  const texto =
    String(value).trim();

  return /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}(?:\s|,|$)/i.test(
    texto
  );
};

/*
  Google puede devolver primero un Plus Code,
  pero en resultados posteriores puede existir
  una vía, carretera o dirección más útil.

  Buscamos primero el resultado más humano.
*/
const resultadoDireccion =
  results.find((result) => {
    const formatted =
      result?.formatted_address || '';

    const types =
      result?.types || [];

    if (esPlusCode(formatted)) {
      return false;
    }

    return (
      types.includes('street_address') ||
      types.includes('route') ||
      types.includes('premise') ||
      types.includes('subpremise')
    );
  }) ||
  principal;

const components =
  resultadoDireccion.address_components ||
  principal.address_components ||
  [];

/*
  Vía / carretera.
*/
const route =
  obtenerComponenteDireccion(
    components,
    ['route']
  );

const streetNumber =
  obtenerComponenteDireccion(
    components,
    ['street_number']
  );

/*
  Sector rural o urbano.

  En Colombia Google puede identificar una
  zona rural como neighborhood, sublocality
  o incluso administrative_area_level_3.
*/
const sector =
  obtenerComponenteDireccion(
    components,
    [
      'neighborhood',
      'sublocality_level_1',
      'sublocality_level_2',
      'sublocality',
      'administrative_area_level_3'
    ]
  );

const city =
  obtenerComponenteDireccion(
    components,
    [
      'locality',
      'administrative_area_level_2'
    ]
  );

const department =
  obtenerComponenteDireccion(
    components,
    [
      'administrative_area_level_1'
    ]
  );

const country =
  obtenerComponenteDireccion(
    components,
    ['country']
  );

/*
  Dirección principal que verá MACCEZ.

  Prioridad:

  1. Calle/vía + número.
  2. Calle/vía sin número.
  3. Sector/vereda/localidad.
  4. Ciudad.

  Nunca usamos el Plus Code como texto
  principal.
*/
let street = '';

if (route) {
  street =
    [route, streetNumber]
      .filter(Boolean)
      .join(' ');
} else if (sector) {
  street = sector;
} else if (city) {
  street = city;
}

/*
  formattedAddress solo se conserva si
  Google entregó una dirección convencional.

  Si contiene Plus Code lo descartamos.
*/
const formattedAddressOriginal =
  resultadoDireccion?.formatted_address ||
  principal?.formatted_address ||
  '';

const formattedAddress =
  esPlusCode(formattedAddressOriginal)
    ? ''
    : formattedAddressOriginal;
    /*
      Intentamos aprovechar un punto de
      referencia que venga incluido en la
      respuesta de Google.

      Si Google no devuelve uno, simplemente
      no mostramos "Cerca de".
    */
    const referenceResult =
      results.find((result) => {
        const types =
          result.types || [];

        return (
          types.includes('point_of_interest') ||
          types.includes('establishment') ||
          types.includes('premise')
        );
      });

    let reference = '';

    if (referenceResult) {
      const primerFragmento =
        String(
          referenceResult.formatted_address ||
          ''
        )
          .split(',')[0]
          .trim();

      if (
        primerFragmento &&
        primerFragmento !== street
      ) {
        reference =
          primerFragmento;
      }
    }

setVehicleLocation({
  street,
  sector,
  city,
  department,
  country,
  reference,
  formattedAddress
});
  } catch (error) {
    console.error(
      'ERROR REVERSE GEOCODING:',
      error
    );

    if (
      requestId ===
      locationRequestRef.current
    ) {
      setVehicleLocation(null);
    }

  } finally {
    if (
      requestId ===
      locationRequestRef.current
    ) {
      setLocationLoading(false);
    }
  }
}
/*
  ENCUADRE INICIAL DEL MAPA

  - Varios activos: muestra todos.
  - Un activo: muestra la ciudad.
  - Se ejecuta una sola vez después de
    recibir posiciones GPS válidas.
*/
useEffect(() => {
  if (!map) {
    return;
  }

  if (initialViewportAppliedRef.current) {
    return;
  }

  if (
    modoHistoricoActivo ||
    rutaHistorica.length > 0
  ) {
    return;
  }

  const posicionesValidas = vehicles
    .map((vehicle) => {
      const lat = Number(
        vehicle.lat ??
        vehicle.latitude
      );

      const lng = Number(
        vehicle.lng ??
        vehicle.longitude
      );

      return {
        lat,
        lng
      };
    })
    .filter((position) => {
      return (
        Number.isFinite(position.lat) &&
        Number.isFinite(position.lng) &&
        position.lat !== 0 &&
        position.lng !== 0
      );
    });

  if (posicionesValidas.length === 0) {
    return;
  }

  initialViewportAppliedRef.current =
    true;

  /*
    Registramos la selección inicial para
    evitar que sea interpretada como una
    selección manual del usuario.
  */
  lastSelectedIdRef.current =
    vehiculoSeleccionado
      ? getVehicleMapKey(
          vehiculoSeleccionado
        )
      : null;

  lastCameraSelectionTokenRef.current =
    selectionToken;

  ignorarEventoMapaRef.current =
    true;

  /*
    Un único activo:
    mostramos su ciudad y sus alrededores.
  */
  if (posicionesValidas.length === 1) {
    map.panTo(
      posicionesValidas[0]
    );

    map.setZoom(11.5);
  } else {
    /*
      Varios activos:
      construimos un encuadre que los
      mantenga a todos visibles.
    */
    const bounds =
      new window.google.maps.LatLngBounds();

    posicionesValidas.forEach(
      (position) => {
        bounds.extend(position);
      }
    );

    const mobile =
      isMobileLayout();

    map.fitBounds(
      bounds,
      mobile
        ? {
            top: 210,
            right: 45,
            bottom: 135,
            left: 45
          }
        : {
            top: 90,
            right: 90,
            bottom: 90,
            left: 90
          }
    );

    /*
      Si los activos están muy juntos,
      fitBounds podría acercarse demasiado.
      Limitamos la vista inicial a zoom 13.
    */
    window.google.maps.event.addListenerOnce(
      map,
      'idle',
      () => {
        const zoomActual =
          Number(map.getZoom());

        if (
          Number.isFinite(zoomActual) &&
          zoomActual > 13
        ) {
          map.setZoom(13);
        }
      }
    );
  }

  setTimeout(() => {
    ignorarEventoMapaRef.current =
      false;
  }, 800);

}, [
  map,
  vehicles,
  modoHistoricoActivo,
  rutaHistorica.length,
  selectionToken
]);

/*
  ACERCAMIENTO AL SELECCIONAR UN ACTIVO

  Mantiene el activo en el mismo punto visual
  durante toda la animación de zoom.
*/
useEffect(() => {
  if (!map || !vehiculoSeleccionado) {
    return;
  }

  if (!initialViewportAppliedRef.current) {
    return;
  }

  if (!selectionToken) {
    return;
  }

  if (
    selectionToken ===
    lastCameraSelectionTokenRef.current
  ) {
    return;
  }

  const lat = Number(
    vehiculoSeleccionado.lat ??
    vehiculoSeleccionado.latitude
  );

  const lng = Number(
    vehiculoSeleccionado.lng ??
    vehiculoSeleccionado.longitude
  );

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat === 0 ||
    lng === 0
  ) {
    return;
  }

  lastCameraSelectionTokenRef.current =
    selectionToken;

  lastSelectedIdRef.current =
    getVehicleMapKey(
      vehiculoSeleccionado
    );

  if (vehicleZoomTimerRef.current) {
    clearTimeout(
      vehicleZoomTimerRef.current
    );

    vehicleZoomTimerRef.current =
      null;
  }

  ignorarEventoMapaRef.current =
    true;

  // La selección y la brújula recuperan la vista al norte antes del zoom.
  if (isMobileLayout() && !modoHistoricoActivo) {
    mapHeadingRef.current = 0;
    applyCameraHeading(0);
    manualCameraNotifiedRef.current = false;
    setReturnToAssetVisible(false);
    map.setHeading(0);
    map.setTilt(0);
  }

  const zoomInicial =
    Number(map.getZoom()) || 12;

  const zoomDestino = 16;
  const cantidadPasos = 8;

  /*
    Devuelve el centro geográfico necesario
    para que el activo permanezca siempre en
    el mismo punto visual.

    Los 42 píxeles reproducen la posición final
    que anteriormente conseguíamos mediante:

    map.panBy(0, -42)
  */
  const obtenerCentroCamara = (
    zoomActual
  ) => {
    const position = {
      lat,
      lng
    };

    if (
      !isMobileLayout()
    ) {
      return position;
    }

    const projection =
      map.getProjection();

    if (
      !projection ||
      !window.google?.maps
    ) {
      return position;
    }

    const puntoVehiculo =
      projection.fromLatLngToPoint(
        new window.google.maps.LatLng(
          lat,
          lng
        )
      );

    if (!puntoVehiculo) {
      return position;
    }

    const escala =
      Math.pow(
        2,
        zoomActual
      );

    /*
      Movemos el centro geográfico hacia arriba.
      Como resultado, el activo aparece 42 px
      más abajo, alineado con la flecha.
    */
    const puntoCentro =
      new window.google.maps.Point(
        puntoVehiculo.x,
        puntoVehiculo.y -
          50 / escala
      );

    const centroGeografico =
      projection.fromPointToLatLng(
        puntoCentro
      );

    if (!centroGeografico) {
      return position;
    }

    return {
      lat:
        centroGeografico.lat(),

      lng:
        centroGeografico.lng()
    };
  };

  /*
    Antes de iniciar el zoom ubicamos el activo
    directamente en su posición visual final.
  */
  map.panTo(
    obtenerCentroCamara(
      zoomInicial
    )
  );

  let pasoActual = 0;

  const animarAcercamiento = () => {
    pasoActual += 1;

    const progreso =
      pasoActual /
      cantidadPasos;

    const progresoSuavizado =
      1 -
      Math.pow(
        1 - progreso,
        3
      );

    const nuevoZoom =
      zoomInicial +
      (
        zoomDestino -
        zoomInicial
      ) *
      progresoSuavizado;

    /*
      Centro y zoom cambian juntos.

      Esto evita que el vehículo quede arriba
      durante el zoom y baje al finalizar.
    */
    map.moveCamera({
      center:
        obtenerCentroCamara(
          nuevoZoom
        ),

      zoom:
        nuevoZoom
    });

    if (
      pasoActual <
      cantidadPasos
    ) {
      vehicleZoomTimerRef.current =
        setTimeout(
          animarAcercamiento,
          95
        );

      return;
    }

    map.moveCamera({
      center:
        obtenerCentroCamara(
          zoomDestino
        ),

      zoom:
        zoomDestino
    });

    vehicleZoomTimerRef.current =
      setTimeout(() => {
        ignorarEventoMapaRef.current =
          false;

        vehicleZoomTimerRef.current =
          null;
      }, 250);
  };

  vehicleZoomTimerRef.current =
    setTimeout(
      animarAcercamiento,
      180
    );

  return () => {
    if (vehicleZoomTimerRef.current) {
      clearTimeout(
        vehicleZoomTimerRef.current
      );

      vehicleZoomTimerRef.current =
        null;
    }
  };

}, [
  map,
  selectionToken,
  applyCameraHeading,
  modoHistoricoActivo,
  vehiculoSeleccionado
    ? getVehicleMapKey(
        vehiculoSeleccionado
      )
    : ''
]);

useEffect(() => {
  if (!selectionToken) {
    return;
  }

  if (!vehiculoSeleccionado) {
    return;
  }

  if (
    selectionToken ===
    lastLocationSelectionRef.current
  ) {
    return;
  }

lastLocationSelectionRef.current =
  selectionToken;

/*
  Cada selección explícita del activo,
  incluso si es el mismo activo,
  reactiva su seguimiento en vivo.
*/
setLiveVehicleFollow(true);

/*
  La nueva animación de selección ya está
  posicionando correctamente la cámara.

  Registramos esta posición como la primera
  alineación del seguimiento para evitar que
  el efecto de seguimiento ejecute después
  un segundo panTo y baje el vehículo.
*/
const latSeguimientoInicial = Number(
  vehiculoSeleccionado.lat ??
  vehiculoSeleccionado.latitude
);

const lngSeguimientoInicial = Number(
  vehiculoSeleccionado.lng ??
  vehiculoSeleccionado.longitude
);

lastLiveFollowPositionRef.current =
  Number.isFinite(latSeguimientoInicial) &&
  Number.isFinite(lngSeguimientoInicial) &&
  latSeguimientoInicial !== 0 &&
  lngSeguimientoInicial !== 0
    ? {
        lat: latSeguimientoInicial,
        lng: lngSeguimientoInicial
      }
    : null;

setReturnToAssetVisible(false);
setLocationBarVisible(true);

  const lat =
    vehiculoSeleccionado.lat ??
    vehiculoSeleccionado.latitude;

  const lng =
    vehiculoSeleccionado.lng ??
    vehiculoSeleccionado.longitude;

  /*
    Si el activo todavía no tiene posición,
    la franja igualmente aparece.
  */
  if (lat == null || lng == null) {
    setLocationLoading(false);

    setVehicleLocation({
      street: 'Sin ubicación GPS disponible',
      sector: '',
      city: '',
      department: '',
      country: '',
      reference: '',
      formattedAddress: ''
    });

    return;
  }

  const latitude = Number(lat);
  const longitude = Number(lng);

  if (
    Number.isNaN(latitude) ||
    Number.isNaN(longitude)
  ) {
    setLocationLoading(false);

    setVehicleLocation({
      street: 'Sin ubicación GPS disponible',
      sector: '',
      city: '',
      department: '',
      country: '',
      reference: '',
      formattedAddress: ''
    });

    return;
  }

  /*
    Ahora sí consultamos Google para
    convertir las coordenadas en dirección.
  */
  obtenerDireccionVehiculo(
    latitude,
    longitude
  );

}, [
  selectionToken
]);

useEffect(() => {
  if (!map) {
    return;
  }

  if (!liveVehicleFollow) {
    return;
  }

  if (!vehiculoSeleccionado) {
    return;
  }

  if (rutaHistorica.length > 0) {
    return;
  }

  const selectedId =
    getVehicleMapKey(
      vehiculoSeleccionado
    );

  const currentVehicle =
    vehicles.find((vehicle) => {
      return (
        String(
          getVehicleMapKey(vehicle)
        ) === String(selectedId)
      );
    }) ||
    vehiculoSeleccionado;

  const lat =
    currentVehicle.lat ??
    currentVehicle.latitude;

  const lng =
    currentVehicle.lng ??
    currentVehicle.longitude;

  if (
    lat == null ||
    lng == null
  ) {
    return;
  }

  const latitude =
    Number(lat);

  const longitude =
    Number(lng);

  if (
    Number.isNaN(latitude) ||
    Number.isNaN(longitude) ||
    latitude === 0 ||
    longitude === 0
  ) {
    return;
  }

  const ultimaPosicion =
    lastLiveFollowPositionRef.current;

  const primeraAlineacion =
    !ultimaPosicion;

  if (
    !primeraAlineacion &&
    Math.abs(
      ultimaPosicion.lat -
        latitude
    ) < 0.000001 &&
    Math.abs(
      ultimaPosicion.lng -
        longitude
    ) < 0.000001
  ) {
    return;
  }

  lastLiveFollowPositionRef.current = {
    lat: latitude,
    lng: longitude
  };

  /*
    Al seleccionar o reactivar el seguimiento,
    sí ubicamos inicialmente el activo.
  */
  if (primeraAlineacion) {
    ignorarEventoMapaRef.current =
      true;

    lastCameraAdjustmentRef.current =
      0;

    map.panTo({
      lat: latitude,
      lng: longitude
    });

    if (
      isMobileLayout()
    ) {
      setTimeout(() => {
        map.panBy(0, -52);
      }, 80);
    }

    setTimeout(() => {
      ignorarEventoMapaRef.current =
        false;
    }, 450);

    return;
  }

  /*
    Después de la alineación inicial, dejamos
    que el vehículo se desplace por la pantalla.

    Medimos la posición que actualmente está
    mostrando la animación, no solamente el
    siguiente punto GPS.
  */
  const animatedPosition =
    vehiculosAnimadosRef.current[
      selectedId
    ];

  const visiblePosition = {
    lat: Number(
      animatedPosition?.lat ??
      latitude
    ),

    lng: Number(
      animatedPosition?.lng ??
      longitude
    )
  };

  if (
    Number.isNaN(visiblePosition.lat) ||
    Number.isNaN(visiblePosition.lng)
  ) {
    return;
  }

  const projection =
    map.getProjection();

  const bounds =
    map.getBounds();

  const mapDiv =
    map.getDiv();

  const zoom =
    map.getZoom();

  if (
    !projection ||
    !bounds ||
    !mapDiv ||
    zoom == null
  ) {
    return;
  }

  const northEast =
    bounds.getNorthEast();

  const southWest =
    bounds.getSouthWest();

  const topRight =
    projection.fromLatLngToPoint(
      northEast
    );

  const bottomLeft =
    projection.fromLatLngToPoint(
      southWest
    );

  const vehiclePoint =
    projection.fromLatLngToPoint(
      new window.google.maps.LatLng(
        visiblePosition.lat,
        visiblePosition.lng
      )
    );

  if (
    !topRight ||
    !bottomLeft ||
    !vehiclePoint
  ) {
    return;
  }

  const scale =
    Math.pow(2, zoom);

  const vehiclePixel = {
    x:
      (vehiclePoint.x -
        bottomLeft.x) *
      scale,

    y:
      (vehiclePoint.y -
        topRight.y) *
      scale
  };

  const mobile =
    isMobileLayout();

  /*
    Límites amplios de seguimiento.

    El mapa no se mueve mientras el vehículo
    permanezca dentro de este rectángulo.
  */
  const safeLimits = {
    left:
      mobile ? 58 : 70,

    right:
      mapDiv.clientWidth -
      (mobile ? 58 : 70),

    /*
      En móvil protegemos el carrusel superior.
    */
    top:
      mobile ? 190 : 70,

    /*
      En móvil protegemos ubicación, controles
      y navegación inferior.
    */
    bottom:
      mapDiv.clientHeight -
      (mobile ? 138 : 70)
  };

  const outsideSafeZone =
    vehiclePixel.x <
      safeLimits.left ||

    vehiclePixel.x >
      safeLimits.right ||

    vehiclePixel.y <
      safeLimits.top ||

    vehiclePixel.y >
      safeLimits.bottom;

  /*
    Dentro de la zona segura la cámara queda
    completamente quieta.
  */
  if (!outsideSafeZone) {
    return;
  }

  const now =
    Date.now();

  /*
    Evita múltiples correcciones seguidas
    si llegan varios reportes GPS juntos.
  */
  if (
    now -
      lastCameraAdjustmentRef.current <
    900
  ) {
    return;
  }

  const targetPixel = {
    x:
      mapDiv.clientWidth / 2,

    y:
      mapDiv.clientHeight *
      (mobile ? 0.555 : 0.5)
  };

  const dx =
    vehiclePixel.x -
    targetPixel.x;

  const dy =
    vehiclePixel.y -
    targetPixel.y;

  lastCameraAdjustmentRef.current =
    now;

  ignorarEventoMapaRef.current =
    true;

  /*
    El vehículo llegó al límite permitido.
    Hacemos una sola corrección para devolverlo
    a la zona central.
  */
  map.panBy(
    dx,
    dy
  );

  setTimeout(() => {
    ignorarEventoMapaRef.current =
      false;
  }, 450);

}, [
  map,
  vehicles,
  liveVehicleFollow,
  rutaHistorica.length,
  selectionToken,
  vehiculoSeleccionado
    ? getVehicleMapKey(
        vehiculoSeleccionado
      )
    : ''
]);
  useEffect(() => {
  if (!map) return;
  if (rutaHistorica.length === 0) return;

 const mobile =
  isMobileLayout();

playbackStartedRef.current = false;
playbackManualControlRef.current = false;

/*
  En móvil iniciamos mostrando toda la ruta.
  El usuario puede activar después
  "Seguir vehículo" si desea acercarse.
*/
setPlaybackFollow(!mobile);
  const bounds = new window.google.maps.LatLngBounds();

  rutaHistorica.forEach((p) => {
    bounds.extend(p);
  });

  if (marcadoresHistoricos.inicio) {
    bounds.extend(marcadoresHistoricos.inicio);
  }

  if (marcadoresHistoricos.fin) {
    bounds.extend(marcadoresHistoricos.fin);
  }

  detencionesHistoricas.forEach((p) => {
    bounds.extend({
      lat: Number(p.lat),
      lng: Number(p.lng)
    });
  });

  ignorarEventoMapaRef.current = true;

 map.fitBounds(
  bounds,

  mobile
    ? {
        top: 105,
        right: 32,
        bottom: 280,
        left: 32
      }
    : {
        top: 120,
        right: 420,
        bottom: 80,
        left: 80
      }
);

  setTimeout(() => {
    ignorarEventoMapaRef.current = false;
  }, 700);
}, [map, rutaHistorica, marcadoresHistoricos, detencionesHistoricas]);
useEffect(() => {
  if (!map) return;
  if (rutaHistorica.length === 0) return;
  if (!playbackFollow) return;
  if (!playbackPosition) return;

  const puntoActual = {
    lat: Number(playbackPosition.lat),
    lng: Number(playbackPosition.lng)
  };

  if (
    Number.isNaN(puntoActual.lat) ||
    Number.isNaN(puntoActual.lng)
  ) {
    return;
  }

  /*
    Primer movimiento después de presionar Play:
    centramos el vehículo y aplicamos un zoom
    moderado, sin acercarnos demasiado.
  */
  if (!playbackStartedRef.current) {
    playbackStartedRef.current = true;

    ignorarEventoMapaRef.current = true;

    map.moveCamera({
      center: puntoActual,
      zoom: 15.5
    });

    setTimeout(() => {
      ignorarEventoMapaRef.current = false;
    }, 600);

    return;
  }

  /*
    Después del zoom inicial no movemos el mapa
    constantemente. Solamente comprobamos si el
    vehículo se acerca a los límites visibles.
  */
  const projection =
    map.getProjection();

  const bounds =
    map.getBounds();

  const mapDiv =
    map.getDiv();

  const zoom =
    map.getZoom();

  if (
    !projection ||
    !bounds ||
    !mapDiv ||
    zoom == null
  ) {
    return;
  }

  const northEast =
    bounds.getNorthEast();

  const southWest =
    bounds.getSouthWest();

  const topRight =
    projection.fromLatLngToPoint(
      northEast
    );

  const bottomLeft =
    projection.fromLatLngToPoint(
      southWest
    );

  const vehiclePoint =
    projection.fromLatLngToPoint(
      new window.google.maps.LatLng(
        puntoActual.lat,
        puntoActual.lng
      )
    );

  if (
    !topRight ||
    !bottomLeft ||
    !vehiclePoint
  ) {
    return;
  }

  const scale =
    Math.pow(2, zoom);

  const vehiclePixel = {
    x:
      (vehiclePoint.x -
        bottomLeft.x) *
      scale,

    y:
      (vehiclePoint.y -
        topRight.y) *
      scale
  };

  const mobile =
    isMobileLayout();

  /*
    Zona segura del mapa.

    En móvil dejamos libre la parte inferior
    porque allí se encuentra el controlador
    del recorrido.
  */
  const safeLimits = {
    left:
      mobile ? 45 : 70,

    right:
      mapDiv.clientWidth -
      (mobile ? 45 : 70),

    top:
      mobile ? 70 : 70,

    bottom:
      mapDiv.clientHeight -
      (mobile ? 265 : 70)
  };

  const fueraDeZona =
    vehiclePixel.x < safeLimits.left ||
    vehiclePixel.x > safeLimits.right ||
    vehiclePixel.y < safeLimits.top ||
    vehiclePixel.y > safeLimits.bottom;

  /*
    Si el vehículo continúa visible,
    la cámara permanece completamente quieta.
  */
  if (!fueraDeZona) {
    return;
  }

  const ahora =
    Date.now();

  /*
    Evitamos varias correcciones consecutivas.
  */
  if (
    ahora -
      lastCameraAdjustmentRef.current <
    900
  ) {
    return;
  }

  const targetPixel = {
    x:
      mapDiv.clientWidth / 2,

    y:
      mobile
        ? (
            safeLimits.top +
            safeLimits.bottom
          ) / 2
        : mapDiv.clientHeight / 2
  };

  const dx =
    vehiclePixel.x -
    targetPixel.x;

  const dy =
    vehiclePixel.y -
    targetPixel.y;

  lastCameraAdjustmentRef.current =
    ahora;

  ignorarEventoMapaRef.current = true;

  map.panBy(
    dx,
    dy
  );

  setTimeout(() => {
    ignorarEventoMapaRef.current = false;
  }, 450);
}, [
  map,
  playbackPosition,
  rutaHistorica.length,
  playbackFollow
]);

  useEffect(() => {
    if (!vehicles || vehicles.length === 0) return;

    vehicles.forEach((vehicle) => {
   const id = getVehicleMapKey(vehicle);

      const lat = Number(vehicle.lat ?? vehicle.latitude);
      const lng = Number(vehicle.lng ?? vehicle.longitude);

      if (!id || !lat || !lng) return;

      targetsRef.current[id] = { lat, lng };

      const posicionAnterior = ultimasPosicionesRef.current[id];
      const posicionNueva = { lat, lng };

      let courseCalculado = null;

      if (posicionAnterior) {
        const distancia = calcularDistanciaMetros(
          posicionAnterior.lat,
          posicionAnterior.lng,
          posicionNueva.lat,
          posicionNueva.lng
        );

        if (distancia >= 5) {
          courseCalculado = calcularCourseEntrePuntos(
            posicionAnterior,
            posicionNueva
          );
        }
      }

      if (courseCalculado !== null) {
        cursosRef.current[id] = courseCalculado;
      }

      ultimasPosicionesRef.current[id] = posicionNueva;
    });

    setVehiculosAnimados((prev) => {
      const next = { ...prev };

   vehicles.forEach((vehicle) => {
  const id = getVehicleMapKey(vehicle);
        const target = targetsRef.current[id];

        if (!id || !target) return;

        if (!next[id]) {
          next[id] = target;
        }
      });

      return next;
    });
  }, [vehicles]);


useEffect(() => {
  const animar = () => {


    setVehiculosAnimados((prev) => {



        const next = { ...prev };
        let hayMovimiento = false;

        Object.keys(targetsRef.current).forEach((id) => {
          const actual = prev[id];
          const destino = targetsRef.current[id];

          if (!actual || !destino) return;

          const diffLat = destino.lat - actual.lat;
          const diffLng = destino.lng - actual.lng;

          if (
            Math.abs(diffLat) > 0.000001 ||
            Math.abs(diffLng) > 0.000001
          ) {
            next[id] = {
              lat: actual.lat + diffLat * 0.08,
              lng: actual.lng + diffLng * 0.08
            };

            hayMovimiento = true;
          } else {
            next[id] = destino;
          }

          const cursoDestino = cursosRef.current[id];

          if (cursoDestino !== undefined) {
            const cursoActual =
              cursosAnimadosRef.current[id] ?? cursoDestino;

            cursosAnimadosRef.current[id] =
              interpolarAngulo(cursoActual, cursoDestino, 0.08);
          }
        });

        return hayMovimiento ? next : prev;
      });

      animationRef.current = requestAnimationFrame(animar);
    };

    animationRef.current = requestAnimationFrame(animar);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);


  const rutaCompleta = rutaHistorica;
const rutaRecorrida = useMemo(() => {
  if (playbackTravelledPath && playbackTravelledPath.length > 1) {
    return playbackTravelledPath.map((p) => ({
      lat: Number(p.lat),
      lng: Number(p.lng)
    }));
  }

  if (!playbackPosition) return [];

  return [
    ...rutaHistorica.slice(0, playbackIndex + 1),
    {
      lat: Number(playbackPosition.lat),
      lng: Number(playbackPosition.lng)
    }
  ];
}, [
  playbackTravelledPath,
  playbackPosition,
  rutaHistorica,
  playbackIndex
]);

  // Solo se envían opciones nuevas cuando cambia el modo del mapa.
  const rotationMobileLayout = isMobileLayout();
  const stableMapOptions = useMemo(() => ({
    mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
    headingInteractionEnabled: rotationMobileLayout && !modoHistoricoActivo,
    tiltInteractionEnabled: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
    keyboardShortcuts: false,
    draggable: true,
    gestureHandling: 'greedy',
    padding: rotationMobileLayout && !modoHistoricoActivo
      ? {top: 190, right: 0, bottom: 105, left: 0}
      : {top: 0, right: 0, bottom: 0, left: 0}
  }), [rotationMobileLayout, modoHistoricoActivo]);

  rotationContextRef.current = {
    mobile: rotationMobileLayout,
    history: modoHistoricoActivo,
    streetView: streetViewActive,
    selected: Boolean(vehiculoSeleccionado)
  };

  useEffect(() => {
    if (!map) return;
    const notifyManualCamera = () => {
      const context = rotationContextRef.current;
      if (!context?.mobile || context.history || context.streetView) return;
      if (manualCameraNotifiedRef.current) return;
      manualCameraNotifiedRef.current = true;
      setLiveVehicleFollow(false);
      setLocationBarVisible(false);
      setTrackingNavigationVisible(false);
      if (context.selected) setReturnToAssetVisible(true);
    };
    const updateHeading = () => {
      const heading = Number(map.getHeading()) || 0;
      const previous = mapHeadingRef.current;
      applyCameraHeading(heading);
      const difference = Math.abs(((heading - previous + 540) % 360) - 180);
      if (difference < 0.01 || ignorarEventoMapaRef.current) return;
      notifyManualCamera();
    };
    const beginTouchGesture = (event) => {
      const context = rotationContextRef.current;
      if (event.touches.length < 2 || !context?.mobile ||
          context.history || context.streetView) return;
      // El usuario toma el control: cancelar únicamente el zoom de selección.
      if (vehicleZoomTimerRef.current) {
        clearTimeout(vehicleZoomTimerRef.current);
        vehicleZoomTimerRef.current = null;
      }
      ignorarEventoMapaRef.current = false;
      notifyManualCamera();
    };
    const listener = map.addListener('heading_changed', updateHeading);
    const container = map.getDiv();
    container.addEventListener('touchstart', beginTouchGesture, {passive: true});
    applyCameraHeading(Number(map.getHeading()) || 0);
    return () => {
      listener.remove();
      container.removeEventListener('touchstart', beginTouchGesture);
    };
  }, [map, applyCameraHeading]);

  if (!isLoaded) return null;

  const isMobile = isMobileLayout();
  const vehicleIconSize = isMobile ? 46 : 60;

  const grosorRutaCompleta =
  mapZoom <= 10
    ? 2.5
    : mapZoom <= 12
    ? 3.5
    : mapZoom <= 14
    ? 5
    : mapZoom <= 16
    ? 7
    : 9;

const grosorRutaRecorrida =
  mapZoom <= 10
    ? 2
    : mapZoom <= 12
    ? 2.5
    : mapZoom <= 14
    ? 3.5
    : mapZoom <= 16
    ? 5
    : 6.5;

  const renderMarcador = ({
    key,
    position,
    label,
    background,
    title
  }) => (
    <OverlayView
      key={key}
      position={position}
    mapPaneName={OverlayView.OVERLAY_LAYER}
    >
      <div
        title={title}
        style={{
          transform: 'translate(-50%, -110%)',
          position: 'absolute',
          zIndex: 999999,
          pointerEvents: 'auto'
        }}
      >
        <div
          style={{
            minWidth: 54,
            height: 34,
            padding: '0 10px',
            borderRadius: 999,
            background,
            color: 'white',
            border: '3px solid white',
            fontWeight: 900,
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 22px rgba(0,0,0,0.45)'
          }}
        >
          {label}
        </div>
      </div>
    </OverlayView>
  );

  return (
    <>
{isMobile &&
  !modoHistoricoActivo &&
  !ocultarMenuGeocercas &&
  !detalleVehiculoVisible && (
  <div
    style={{
      position: 'fixed',

      /*
        Posición temporal para evaluar la rueda:
        más abajo y más hacia la izquierda.
      */
      top: '55.5%',
      left: '40%',

      zIndex: 99999,
      pointerEvents: 'auto'
    }}
  >
    <VehicleArcMenu
  selectedAssetName={
    vehiculoSeleccionado?.name || ''
  }

  onControlClick={() => {
    if (!vehiculoSeleccionado) {
      return;
    }

    onOpenVehicleDetail?.(
      vehiculoSeleccionado
    );
  }}

onSelect={(item) => {
  if (item?.id === 'history') {
    onOpenHistory?.();
    return;
  }

  if (item?.id === 'events') {
    onOpenEvents?.();
    return;
  }

  if (item?.id === 'geofences') {
    onOpenGeofences?.();
    return;
  }

if (item?.id === 'tracking') {
  if (!vehiculoSeleccionado) {
    return;
  }

  setLocationBarVisible(false);
  setTrackingNavigationVisible(true);

  return;
}
  if (item?.id === 'reports') {
    onOpenReports?.();
    return;
  }

  if (item?.id === 'sentinel') {
    onOpenSentinel?.();
  }
}}
/>

  </div>
)}
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={13}
      mapTypeId={mapType}
      onTilesLoaded={() => onDiviMapReady?.()}
   onLoad={(mapInstance) => {
  setMap(mapInstance);

  setMapZoom(
    Number(mapInstance.getZoom()) ||
      13
  );
}}




onDragStart={() => {
  if (
    ignorarEventoMapaRef.current
  ) {
    return;
  }

  /*
    Si el playback ya comenzó, el usuario
    tomó el control manual del mapa.
  */
  if (
    modoHistoricoActivo &&
    playbackStartedRef.current
  ) {
    playbackManualControlRef.current = true;
  }

  setPlaybackFollow(false);
  setLiveVehicleFollow(false);

  setLocationBarVisible(false);
  setTrackingNavigationVisible(false);
  if (isMobile && !modoHistoricoActivo && vehiculoSeleccionado) {
    setReturnToAssetVisible(true);
  }
}}

onClick={(event) => {
  if (modoCrearGeocerca && event.latLng) {
    setGeocercaTemporal((actual) => ({
      latitude: event.latLng.lat(),
      longitude: event.latLng.lng(),
      radius: actual?.radius ?? 150
    }));
    return;
  }

  setLocationBarVisible(false);
  setTrackingNavigationVisible(false);
  if (isMobile && !modoHistoricoActivo && vehiculoSeleccionado) {
    setReturnToAssetVisible(true);
  }
}}
onIdle={() => {
  const nuevoZoom = Number(map?.getZoom());

  if (Number.isFinite(nuevoZoom)) {
    setMapZoom(nuevoZoom);
  }

  window.dispatchEvent(
    new CustomEvent('maccez:mobile-hub-close')
  );
}}
onZoomChanged={() => {

  /*
    Ignoramos el zoom realizado por nuestro
    propio código al comenzar el playback.
  */
  if (
    ignorarEventoMapaRef.current
  ) {
    return;
  }

  /*
    Después de que el recorrido comenzó,
    un zoom manual entrega el control
    definitivo del mapa al usuario.
  */
  if (
    modoHistoricoActivo &&
    playbackStartedRef.current
  ) {
    playbackManualControlRef.current = true;
  }

  setPlaybackFollow(false);
  setLiveVehicleFollow(false);
  if (isMobile && !modoHistoricoActivo && vehiculoSeleccionado) {
    setLocationBarVisible(false);
    setReturnToAssetVisible(true);
  }
}}
     
options={stableMapOptions}
    >



      {rutaHistorica.length > 1 && (
        <>
          <Polyline
            path={rutaCompleta}
            options={{
              strokeColor: '#38bdf8',
              strokeOpacity: 0.85,
             strokeWeight:
  grosorRutaCompleta,
              zIndex: 9990
            }}
          />

          <Polyline
            path={rutaRecorrida}
            options={{
              strokeColor: '#0f172a',
              strokeOpacity: 0.95,
            strokeWeight:
  grosorRutaRecorrida,
              zIndex: 9991
            }}
          />

          {marcadoresHistoricos.inicio &&
            renderMarcador({
              key: 'marcador-inicio',
              position: marcadoresHistoricos.inicio,
              label: 'Inicio',
              background: '#16a34a',
              title: 'Inicio del recorrido'
            })}

     {detencionesHistoricas.map((parada) =>
  renderMarcador({
    key: `parada-${parada.numero}`,
    position: {
      lat: Number(parada.lat),
      lng: Number(parada.lng)
    },
    label: `P${parada.numero}`,
    background:
      parada.tipo === 'Detenido encendido'
        ? '#2563eb'
        : '#f97316',
    title: `${parada.tipo} ${parada.numero} | ${parada.duracion}`
  })
)}

          {marcadoresHistoricos.fin &&
            renderMarcador({
              key: 'marcador-fin',
              position: marcadoresHistoricos.fin,
              label: 'Fin',
              background: '#dc2626',
              title: 'Fin del recorrido'
            })}
{playbackPosition && (
  <OverlayView
    key="playback-vehicle"
    position={playbackPosition}
    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
  >
<div
  style={{
    width: `${vehicleIconSize}px`,
    height: `${vehicleIconSize}px`,

    transform: `translate(-50%, -50%) rotate(${normalizarAngulo(
      calcularCoursePlayback(
        rutaHistorica,
        playbackIndex
      ) +
        VEHICLE_PLAYBACK_OFFSET
    )}deg)`,

    transformOrigin:
      'center center',

    position: 'absolute',
    zIndex: 999999,
    pointerEvents: 'none'
  }}
>
                <img
                  src={onlineIcon}
                  alt="playback"
                  style={{
                    width: `${vehicleIconSize}px`,
                    height: `${vehicleIconSize}px`,
                    display: 'block',
                    pointerEvents: 'none'
                  }}
                />
              </div>
            </OverlayView>
          )}
        </>
      )}

      {geocercas.map((geo) => (
        <Circle
          key={geo.id}
          center={{
            lat: Number(geo.latitude),
            lng: Number(geo.longitude)
          }}
          radius={Number(geo.radius || 150)}
          options={{
            strokeColor: '#22c55e',
            strokeOpacity: 0.95,
            strokeWeight: 2,
            fillColor: '#22c55e',
            fillOpacity: 0.14
          }}
        />
      ))}

      {geocercaTemporal && (
        <Circle
          center={{
            lat: Number(geocercaTemporal.latitude),
            lng: Number(geocercaTemporal.longitude)
          }}
          radius={Number(geocercaTemporal.radius || 20)}
          options={{
            strokeColor: '#38bdf8',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#38bdf8',
            fillOpacity: 0.18
          }}
        />
      )}
{!modoHistoricoActivo && (
  <UserLocationMarker
    location={userLocation}
    streetViewActive={streetViewActive}
  />
)}
   {!modoHistoricoActivo &&
  rutaHistorica.length === 0 &&
  vehicles.map((vehicle) => {
       const id = getVehicleMapKey(vehicle);

        const latOriginal = vehicle.lat ?? vehicle.latitude;
        const lngOriginal = vehicle.lng ?? vehicle.longitude;

        const posicionAnimada = vehiculosAnimados[id];

        const lat = posicionAnimada?.lat ?? latOriginal;
        const lng = posicionAnimada?.lng ?? lngOriginal;

        if (lat == null || lng == null) return null;

        const iconUrl = getVehicleIcon(vehicle);

        const course =
          cursosAnimadosRef.current[id] ??
          Number(
            vehicle.course ??
              vehicle.attributes?.course ??
              vehicle.rawAttributes?.course ??
              0
          );

        const rotation = normalizarAngulo(course + VEHICLE_LIVE_OFFSET - mapHeadingRef.current);

        return (
          <OverlayView
            key={id}
            position={{
              lat: Number(lat),
              lng: Number(lng)
            }}
        mapPaneName={OverlayView.OVERLAY_LAYER}
          >
            <div
              ref={(node) => {
                if (node) {
                  liveMarkerHeadingNodesRef.current.set(id, {
                    node, course: course + VEHICLE_LIVE_OFFSET
                  });
                } else {
                  liveMarkerHeadingNodesRef.current.delete(id);
                }
              }}
              onClick={() => {
                setVehiculoSeleccionado({
                  ...vehicle,
                  selectedAt: Date.now()
                });
              }}
style={{
  width: `${vehicleIconSize}px`,
  height: `${vehicleIconSize}px`,

  transform:
    `translate(-50%, -50%) rotate(${rotation}deg)`,

  transformOrigin:
    'center center',

  cursor: 'pointer',
  position: 'absolute',
  zIndex: 10000,
  pointerEvents: 'none'
}}
            >
              <img
                src={iconUrl}
                alt="vehiculo"
                style={{
                  width: `${vehicleIconSize}px`,
                  height: `${vehicleIconSize}px`,
                  display: 'block',
                  pointerEvents: 'none'
                }}
              />
            </div>
          </OverlayView>
        );
      })}

 {/* SATÉLITE / MAPA */}
{isMobile &&
  !modoHistoricoActivo &&
  !ocultarMenuGeocercas &&
  !streetViewActive &&
  !detalleVehiculoVisible && (
  <button
    onClick={() => {
      setMapType((prev) =>
        prev === 'roadmap'
          ? 'satellite'
          : 'roadmap'
      );
    }}
    style={{
      position: 'fixed',
      right: '18px',
      bottom: '202px',
      zIndex: 99999,

      width: '42px',
      height: '42px',
      borderRadius: '13px',

      border:
        '1px solid rgba(255,255,255,0.38)',

      background:
        'rgba(255,255,255,0.10)',

      boxShadow:
        '0 4px 14px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.28)',

      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      padding: 0,
      fontSize: '20px',
      cursor: 'pointer'
    }}
    title={
      mapType === 'roadmap'
        ? 'Ver satélite'
        : 'Ver mapa'
    }
  >
    {mapType === 'roadmap' ? '🛰️' : '🗺️'}
  </button>
)}




{/* VOLVER AL ACTIVO · NORTE ARRIBA */}
{isMobile && !modoHistoricoActivo && !ocultarMenuGeocercas &&
  !streetViewActive && !detalleVehiculoVisible &&
  returnToAssetVisible && !locationBarVisible && vehiculoSeleccionado && (
  <button
    type="button"
    aria-label="Volver al activo y orientar al norte"
    title="Volver al activo · Norte arriba"
    onClick={(event) => {
      event.stopPropagation();
      if (!map) return;

      const selectedKey = getVehicleMapKey(vehiculoSeleccionado);
      const current = vehicles.find(vehicle =>
        String(getVehicleMapKey(vehicle)) === String(selectedKey)
      ) || vehiculoSeleccionado;
      const lat = Number(current.lat ?? current.latitude);
      const lng = Number(current.lng ?? current.longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng) ||
          (lat === 0 && lng === 0) ||
          Math.abs(lat) > 90 || Math.abs(lng) > 180) return;

      if (vehicleZoomTimerRef.current) {
        clearTimeout(vehicleZoomTimerRef.current);
        vehicleZoomTimerRef.current = null;
      }

      ignorarEventoMapaRef.current = true;
      manualCameraNotifiedRef.current = false;
      applyCameraHeading(0);

      const zoom = 16;
      let cameraCenter = { lat, lng };
      const projection = map.getProjection();
      if (projection && window.google?.maps) {
        const point = projection.fromLatLngToPoint(
          new window.google.maps.LatLng(lat, lng)
        );
        if (point) {
          const adjusted = projection.fromPointToLatLng(
            new window.google.maps.Point(
              point.x, point.y - 50 / Math.pow(2, zoom)
            )
          );
          if (adjusted) {
            cameraCenter = { lat: adjusted.lat(), lng: adjusted.lng() };
          }
        }
      }

      map.moveCamera({ center: cameraCenter, zoom, heading: 0, tilt: 0 });
      lastLiveFollowPositionRef.current = { lat, lng };
      setLiveVehicleFollow(true);
      setTrackingNavigationVisible(false);
      setLocationBarVisible(true);
      setReturnToAssetVisible(false);
      obtenerDireccionVehiculo(lat, lng);

      vehicleZoomTimerRef.current = setTimeout(() => {
        ignorarEventoMapaRef.current = false;
        vehicleZoomTimerRef.current = null;
      }, 250);
    }}
    style={{
      position: 'fixed', right: '18px', bottom: '94px', zIndex: 99999,
      width: '42px', height: '42px', borderRadius: '13px', padding: 0,
      border: '1px solid rgba(131,211,255,0.65)',
      background: 'rgba(248,252,255,0.92)', color: '#063b78',
      boxShadow: '0 4px 14px rgba(0,0,0,0.14)',
      backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer'
    }}
  >
    <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="17" r="11" fill="#edf7ff" stroke="#7bbce7" />
      <text x="16" y="7" textAnchor="middle" fontSize="7" fontWeight="800" fill="#063b78">N</text>
      <g ref={compassNeedleRef} transform={`rotate(${-mapHeadingRef.current} 16 17)`}>
        <path d="M16 8 L20 17 L16 15 L12 17 Z" fill="#ef4444" />
        <path d="M16 26 L12 17 L16 19 L20 17 Z" fill="#1555a4" />
      </g>
      <circle cx="16" cy="17" r="2" fill="#ffffff" stroke="#063b78" />
    </svg>
  </button>
)}

{/* VOLVER DESDE STREET VIEW */}
{isMobile &&
!modoHistoricoActivo &&
  streetViewActive &&
  !detalleVehiculoVisible && (
  <button
    onClick={() => {
      if (!map) return;

      const panorama = map.getStreetView();

      const estadoAnterior =
        mapaAntesStreetViewRef.current;

      panorama.setVisible(false);

      if (
        !estadoAnterior ||
        !estadoAnterior.center ||
        estadoAnterior.zoom == null
      ) {
        return;
      }

      setTimeout(() => {
        window.google.maps.event.trigger(
          map,
          'resize'
        );

        // Restaurar el giro sin interpretarlo como un gesto nuevo.
        mapHeadingRef.current = Number(estadoAnterior.heading) || 0;
        applyCameraHeading(mapHeadingRef.current);
        map.moveCamera({
          center: estadoAnterior.center,
          zoom: estadoAnterior.zoom,
          heading: mapHeadingRef.current,
          tilt: Number(estadoAnterior.tilt) || 0
        });

        setTimeout(() => {
          if (
            !estadoAnterior.assetPixel ||
            Math.abs(Number(estadoAnterior.heading) || 0) > 0.01 ||
            !vehiculoSeleccionado
          ) {
            return;
          }

          const lat =
            vehiculoSeleccionado.lat ??
            vehiculoSeleccionado.latitude;

          const lng =
            vehiculoSeleccionado.lng ??
            vehiculoSeleccionado.longitude;

          if (
            lat == null ||
            lng == null
          ) {
            return;
          }

          const projection =
            map.getProjection();

          const bounds =
            map.getBounds();

          if (
            !projection ||
            !bounds
          ) {
            return;
          }

          const ne =
            bounds.getNorthEast();

          const sw =
            bounds.getSouthWest();

          const topRight =
            projection.fromLatLngToPoint(ne);

          const bottomLeft =
            projection.fromLatLngToPoint(sw);

          const assetPoint =
            projection.fromLatLngToPoint(
              new window.google.maps.LatLng(
                Number(lat),
                Number(lng)
              )
            );

          if (
            !topRight ||
            !bottomLeft ||
            !assetPoint
          ) {
            return;
          }

          const scale =
            Math.pow(
              2,
              map.getZoom()
            );

          const currentPixel = {
            x:
              (assetPoint.x -
                bottomLeft.x) *
              scale,
            y:
              (assetPoint.y -
                topRight.y) *
              scale
          };

          const dx =
            currentPixel.x -
            estadoAnterior.assetPixel.x;

          const dy =
            currentPixel.y -
            estadoAnterior.assetPixel.y;

          if (
            Math.abs(dx) > 1 ||
            Math.abs(dy) > 1
          ) {
            map.panBy(
              dx,
              dy
            );
          }
        }, 300);
      }, 300);
    }}

    style={{
      position: 'fixed',
      right: '18px',
      bottom: '88px',
      zIndex: 999999,

      height: '36px',
      padding: '0 11px',

      borderRadius: '12px',

      border:
        '1px solid rgba(255,255,255,0.38)',

      background:
        'rgba(255,255,255,0.10)',

      boxShadow:
        '0 4px 14px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.28)',

      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',

      color: '#ffffff',

      fontSize: '11px',
      fontWeight: 700,

      cursor: 'pointer',
      whiteSpace: 'nowrap'
    }}
  >
    ← Volver al mapa
  </button>
)}


{/* STREET VIEW */}
{isMobile &&
  !modoHistoricoActivo &&
  !ocultarMenuGeocercas &&
  !streetViewActive &&
  !detalleVehiculoVisible && (
  <button
    onClick={abrirStreetView}
    style={{
      position: 'fixed',
      right: '18px',
      bottom: '148px',
      zIndex: 99999,

      width: '42px',
      height: '42px',
      borderRadius: '13px',

      border:
        '1px solid rgba(255,255,255,0.38)',

      background:
        'rgba(255,255,255,0.10)',

      boxShadow:
        '0 4px 14px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.28)',

      backdropFilter: 'blur(3px)',
      WebkitBackdropFilter: 'blur(3px)',

      color: '#ffffff',

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      padding: 0,
      cursor: 'pointer'
    }}
    title="Street View"
  >
    <svg
      width="25"
      height="25"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="12"
        cy="5.2"
        r="2.6"
        fill="#F9C928"
      />

      <path
        d="M8.2 10.2C8.2 8.8 9.35 7.7 10.75 7.7H13.25C14.65 7.7 15.8 8.8 15.8 10.2V14.3L14.2 15.4V21H11.9V16.1H11.6V21H9.3V15.4L8.2 14.3V10.2Z"
        fill="#F9C928"
      />

      <path
        d="M8.2 10.5L5.7 13"
        stroke="#F9C928"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <path
        d="M15.8 10.5L18.3 13"
        stroke="#F9C928"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  </button>
)}
</GoogleMap>
{modoCrearGeocerca && (
  <button
    type="button"
    onClick={() => {
      const centroActual = map?.getCenter();

      if (!centroActual) return;

      setGeocercaTemporal((actual) => ({
        latitude: centroActual.lat(),
        longitude: centroActual.lng(),
        radius: actual?.radius ?? 150
      }));
    }}
    style={{
      position: 'fixed',
      left: isMobile ? '60px' : '320px',
      right: isMobile ? '12px' : '380px',
  bottom: isMobile
  ? geocercaTemporal
    ? 'calc(env(safe-area-inset-bottom, 0px) + 40px + min(240px, 36dvh) + 88px)'
    : 'calc(env(safe-area-inset-bottom, 0px) + 40px + min(240px, 36dvh) + 12px)'
  : geocercaTemporal
    ? '100px'
    : '24px',
      zIndex: 10001,
      minHeight: '42px',
      border: '1px solid #93c5fd',
      borderRadius: '12px',
      background: '#1d4ed8',
      color: '#ffffff',
      fontSize: '12px',
      fontWeight: 900,
      cursor: 'pointer'
    }}
  >
    Marcar centro del mapa
  </button>
)}

{modoCrearGeocerca && geocercaTemporal && (
  <div
    style={{
      position: 'fixed',
      left: isMobile ? '60px' : '320px',
      right: isMobile ? '12px' : '380px',
   bottom: isMobile
  ? 'calc(env(safe-area-inset-bottom, 0px) + 40px + min(240px, 36dvh) + 12px)'
  : '24px',
      zIndex: 10000,
      padding: '10px 14px',
      borderRadius: '14px',
      background: 'rgba(9, 37, 74, 0.95)',
      color: '#ffffff',
      boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
    }}
  >
    <label
      htmlFor="radio-lugar-mapa"
      style={{
        display: 'block',
        marginBottom: '6px',
        fontSize: '12px',
        fontWeight: 800
      }}
    >
      Radio del lugar: {Math.round(
        Number(geocercaTemporal.radius) || 150
      )} m
    </label>

<input
  id="radio-lugar-mapa"
  type="range"
  min={radioGeocercaAmplia ? 0 : 30}
  max={radioGeocercaAmplia ? 100 : 1000}
  step={radioGeocercaAmplia ? 1 : 10}
  value={
    radioGeocercaAmplia
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round(
              100 *
              Math.log(
                Math.max(
                  30,
                  Math.min(
                    100000,
                    Number(geocercaTemporal.radius) || 150
                  )
                ) / 30
              ) /
              Math.log(100000 / 30)
            )
          )
        )
      : Math.max(
          30,
          Math.min(
            1000,
            Number(geocercaTemporal.radius) || 150
          )
        )
  }
  onChange={(event) => {
    const valor = Number(event.target.value);

    const nuevoRadio = radioGeocercaAmplia
      ? Math.max(
          30,
          Math.min(
            100000,
            Math.round(
              (30 * Math.pow(100000 / 30, valor / 100)) / 10
            ) * 10
          )
        )
      : valor;

    setGeocercaTemporal((actual) =>
      actual
        ? { ...actual, radius: nuevoRadio }
        : actual
    );
  }}
  style={{ width: '100%' }}
/>
  </div>
)}

{isMobile &&
!modoHistoricoActivo &&
  streetViewMessage &&
  !streetViewActive && (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: '90px',
        transform: 'translateX(-50%)',

        zIndex: 999999,

        padding: '10px 14px',
        borderRadius: '14px',

        background:
          'rgba(30, 41, 59, 0.78)',

        border:
          '1px solid rgba(255,255,255,0.16)',

        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',

        boxShadow:
          '0 8px 24px rgba(15,23,42,0.22)',

        color: '#ffffff',
        fontSize: '11px',
        fontWeight: 800,

        whiteSpace: 'nowrap'
      }}
    >
      Street View no disponible en esta ubicación
    </div>
  )}
{isMobile &&
  !modoHistoricoActivo &&
  !ocultarMenuGeocercas &&
  locationBarVisible &&
  vehiculoSeleccionado &&
  !streetViewActive &&
  !detalleVehiculoVisible && (
    <VehicleLocationBar
      loading={locationLoading}
      location={vehicleLocation}
      onClose={() => {
        setLocationBarVisible(false);
      }}
    />
  )}
{isMobile &&
  !modoHistoricoActivo &&
  !ocultarMenuGeocercas &&
  trackingNavigationVisible &&
  vehiculoSeleccionado &&
  !streetViewActive &&
  !detalleVehiculoVisible && (
    <TrackingNavigationBar
      vehicle={vehiculoSeleccionado}
      onClose={() => {
        setTrackingNavigationVisible(false);
      }}
    />
  )}
  </>
);
}

export default GoogleMapView;